import React from "react";
import { Apple, Star, ArrowRight, Activity, BarChart3, Bell } from "lucide-react";
import { APP_GOOGLE, APP_APPLE, APP_GRAPHIC } from "../data/site";

export default function AppShowcase() {
    return (
        <section
            id="app"
            data-testid="app-section"
            className="relative py-24 sm:py-32 overflow-hidden bg-ink-surface/40 border-y border-white/5"
        >
            <div className="absolute inset-0 bg-grid opacity-30" />
            <div className="absolute -top-32 right-1/3 w-[500px] h-[500px] rounded-full bg-gold/5 blur-3xl" />

            <div className="container mx-auto px-4 sm:px-6 lg:px-10 relative">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    {/* Left: phone mockup with real app graphic */}
                    <div className="lg:col-span-5 order-2 lg:order-1">
                        <div className="relative mx-auto max-w-[320px]">
                            {/* glow */}
                            <div className="absolute inset-0 bg-gold/25 blur-3xl rounded-full" />

                            {/* phone frame */}
                            <div
                                data-testid="app-phone-mockup"
                                className="relative aspect-[9/19] bg-black rounded-[44px] border-[10px] border-zinc-900 shadow-[0_30px_80px_rgba(0,0,0,0.6)] overflow-hidden"
                            >
                                {/* notch */}
                                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-full z-20" />

                                {/* app graphic */}
                                <img
                                    src={APP_GRAPHIC}
                                    alt="App MR Coaching"
                                    className="absolute inset-0 w-full h-full object-cover"
                                />

                                {/* subtle inner ring for premium feel */}
                                <div className="absolute inset-0 ring-1 ring-inset ring-white/5 pointer-events-none rounded-[34px]" />
                            </div>

                            {/* floating tag */}
                            <div className="absolute -right-2 top-12 px-3 py-1.5 rounded-full bg-gold text-ink font-oswald font-bold uppercase text-[10px] tracking-widest shadow-[0_8px_24px_rgba(212,175,55,0.4)] rotate-6 hidden sm:block">
                                iOS · Android
                            </div>
                        </div>
                    </div>

                    {/* Right: copy */}
                    <div className="lg:col-span-7 order-1 lg:order-2">
                        <div className="font-manrope text-sm uppercase tracking-[0.3em] text-gold mb-4">
                            App MR Coaching
                        </div>
                        <h2
                            data-testid="app-title"
                            className="font-oswald font-bold uppercase text-4xl sm:text-5xl lg:text-6xl text-white leading-[1]"
                        >
                            Tutto in tasca.
                            <br />
                            <span className="text-shimmer">Sempre con te.</span>
                        </h2>
                        <p className="mt-6 font-manrope text-zinc-400 text-base sm:text-lg max-w-xl">
                            La tua scheda, i tuoi check, i risultati BIA e la chat con il coach in
                            un'unica app personale. Disponibile per iOS e Android.
                        </p>

                        <div className="mt-8 grid sm:grid-cols-3 gap-3">
                            {[
                                { i: Activity, t: "Schede live" },
                                { i: BarChart3, t: "Progressi & BIA" },
                                { i: Bell, t: "Reminder smart" },
                            ].map((f) => {
                                const I = f.i;
                                return (
                                    <div
                                        key={f.t}
                                        className="flex items-center gap-3 p-3 rounded-lg bg-ink border border-white/5"
                                    >
                                        <I size={16} className="text-gold flex-shrink-0" />
                                        <span className="font-oswald uppercase text-white text-xs tracking-widest">
                                            {f.t}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="mt-8 flex items-center gap-1 text-gold">
                            {[...Array(5)].map((_, i) => (
                                <Star key={i} size={16} className="fill-gold text-gold" />
                            ))}
                            <span className="ml-3 font-manrope text-sm text-zinc-400">
                                Recensioni eccellenti dai nostri atleti
                            </span>
                        </div>

                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <a
                                href={APP_APPLE}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="app-apple-link"
                                className="group flex items-center gap-3 px-6 py-3.5 rounded-xl bg-white text-ink hover:bg-zinc-100 transition-colors"
                            >
                                <Apple size={28} className="fill-ink" />
                                <div className="text-left leading-tight">
                                    <div className="text-[10px] uppercase tracking-widest opacity-70">
                                        Scarica su
                                    </div>
                                    <div className="font-oswald font-bold uppercase text-base">
                                        App Store
                                    </div>
                                </div>
                                <ArrowRight
                                    size={16}
                                    className="ml-2 transition-transform group-hover:translate-x-1"
                                />
                            </a>
                            <a
                                href={APP_GOOGLE}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="app-google-link"
                                className="group flex items-center gap-3 px-6 py-3.5 rounded-xl bg-ink text-white border border-gold/40 hover:bg-gold hover:text-ink hover:border-gold transition-colors"
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    width="26"
                                    height="26"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M3.6 1.6c-.4.3-.6.7-.6 1.3v18.2c0 .6.2 1 .6 1.3l9.7-9.7-9.7-9.1z"
                                        fill="#34A853"
                                    />
                                    <path
                                        d="M16.6 16.4l-3.3-3.3-9.7 9.7c.4.3 1 .3 1.7 0l11.3-6.4z"
                                        fill="#FBBC05"
                                    />
                                    <path
                                        d="M21 11.5l-4.4-2.5-3.3 3.3 3.3 3.3 4.4-2.5c.9-.5.9-1.1 0-1.6z"
                                        fill="#EA4335"
                                    />
                                    <path
                                        d="M4.6 1.4c-.7-.4-1.3-.4-1.7 0L13.3 11.1l3.3-3.3L4.6 1.4z"
                                        fill="#4285F4"
                                    />
                                </svg>
                                <div className="text-left leading-tight">
                                    <div className="text-[10px] uppercase tracking-widest opacity-70">
                                        Disponibile su
                                    </div>
                                    <div className="font-oswald font-bold uppercase text-base">
                                        Google Play
                                    </div>
                                </div>
                                <ArrowRight
                                    size={16}
                                    className="ml-2 transition-transform group-hover:translate-x-1"
                                />
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
